import React, { useMemo } from "react";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import ColumnGroupingTable from "../../../table/ColumnGroupingTable.component.js";
import { Typography } from "@mui/material";
import { useHedgeRatioContext } from "../../../../context/HedgeRatioContext";
import {
  mainResponse,
  HARDCODED_MINIMUM_CASH_BUFER,
  HARDCODED_AVG_ACTUAL_CASH_BUFFER,
} from "../responseApi.js";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

const formatAndLocalize = (num, decimals) => {
  if (typeof num !== "number" || isNaN(num) || num === null) return "0";
  const finalDecimals =
    Number(decimals) >= 0 ? Math.floor(Number(decimals)) : 2;

  try {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: finalDecimals,
      maximumFractionDigits: finalDecimals,
      useGrouping: true,
    }).format(num);
  } catch (error) {
    const factor = Math.pow(10, finalDecimals);
    const roundedNumber = Math.round(num * factor) / factor;
    return roundedNumber.toFixed(finalDecimals).toLocaleString();
  }
};

const roundToDecimals = (num, decimals) => {
  if (typeof num !== "number" || isNaN(num) || num === null) return 0;
  const finalDecimals =
    Number(decimals) >= 0 ? Math.floor(Number(decimals)) : 2;

  const factor = Math.pow(10, finalDecimals);
  const roundedNumber = Math.round(num * factor) / factor;

  if (isFinite(roundedNumber)) {
    return roundedNumber.toFixed(finalDecimals);
  }
  return 0;
};

const filterMonthlyColumns = (columns) => {
  const monthRegex = /^[A-Za-z]{3}-\d{2}$/;

  const filteredColumns = columns.filter((column) => {
    const columnId = column?.id;
    if (typeof columnId === "string" && monthRegex.test(columnId)) {
      return true;
    }
    return false;
  });

  return filteredColumns;
};

const getDaysInMonth = (monthYearId) => {
  const [monthAbbr, yearAbbr] = monthYearId.split("-");
  const monthNames = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11,
  };
  const monthIndex = monthNames[monthAbbr];
  const fullYear = 2000 + parseInt(yearAbbr, 10);

  if (monthIndex === undefined) {
    console.error(`Invalid month abbreviation: ${monthAbbr}`);
    return null;
  }

  return new Date(fullYear, monthIndex + 1, 0).getDate();
};

const getDaysInMonthRow = (monthColumns) => {
  const daysInMonthRow = {
    type: "Timeline",
    category: "No. of Days",
    rowSpan: 0,
    cellStyles: {
      backgroundColor: "#F5F5F5",
      borderBottom: "1px solid #000000",
    },
  };

  monthColumns.forEach((column) => {
    const monthId = column.id;

    if (monthId.match(/^[A-Za-z]{3}-\d{2}$/)) {
      daysInMonthRow[monthId] = getDaysInMonth(monthId);
    }
  });

  return daysInMonthRow;
};

const calculateNAGRow = (fullData, monthKeys, decimals) => {
  const nagCategories = [
    "Lease receipt",
    "Retail receipt",
    "Wholesale receipt",
    "New Lease",
    "New Retail",
    "New Wholesale",
  ];

  const nagRow = {
    type: "(summed from above)",
    category: "NAG",
  };

  const rowsToSum = fullData.filter((row) =>
    nagCategories.includes(row.cashflow)
  );

  monthKeys.forEach((monthKey) => {
    let monthSum = 0;

    rowsToSum.forEach((row) => {
      monthSum += Number(row[monthKey]) || 0;
    });

    nagRow[monthKey] = roundToDecimals(monthSum, decimals);
  });

  return nagRow;
};

const calculateOperIncomeRow = (fullData, monthKeys, decimals) => {
  const operIncomeCategories = ["Other finance receipt", "Interest Expense"];

  const operIncomeRow = {
    type: "(summed from above)",
    category: "Oper. Income",
  };

  const rowsToSum = fullData.filter((row) =>
    operIncomeCategories.includes(row.cashflow)
  );

  monthKeys.forEach((monthKey) => {
    let monthSum = 0;

    rowsToSum.forEach((row) => {
      monthSum += Number(row[monthKey]) || 0;
    });

    operIncomeRow[monthKey] = roundToDecimals(monthSum, decimals);
  });

  return operIncomeRow;
};

const calculateDaysOfAvailableLiquidity = (fullData, monthKeys) => {
  const netLiquidityRow = fullData.find(
    (row) => row.type === "Net Liquidity Position"
  );
  const cumulativeCashflowRow = fullData.find(
    (row) => row.type === "Cumulative Cashflow"
  );
  const netCashflowRow = fullData.find((row) => row.type === "Net Cashflow");

  const daysOfLiquidityRow = {
    type: "Time line",
    category: "DAF 2",
    rowSpan: 2,
    cellStyles: {
      fontWeight: "bold",
      backgroundColor: "#F5F5F5",
      borderTop: "1px solid #000000",
      borderBottom: "1px solid #000000",
    },
  };

  const calculateDenominator = (key, currentIndex) => {
    if (currentIndex === 0) {
      return Number(cumulativeCashflowRow[key]) || 0;
    }
    return Number(netCashflowRow?.[key]) || 0;
  };

  let previousMonthDaysResult = null;
  let previousMonthKey = null;

  monthKeys.forEach((currentMonthKey, index) => {
    const netLiquidityPosition = Number(netLiquidityRow[currentMonthKey]) || 0;
    const denominatorValue = calculateDenominator(currentMonthKey, index);
    const totalDaysInCurrentMonth = getDaysInMonth(currentMonthKey);

    let calculatedValue;

    if (index === 0) {
      // --- Logic for First Month ---
      if (netLiquidityPosition < 0) {
        if (denominatorValue === 0) {
          calculatedValue = 0;
        } else {
          const unroundedDays =
            (-netLiquidityPosition / denominatorValue) *
            totalDaysInCurrentMonth;
          calculatedValue = Math.ceil(unroundedDays); // ROUNDUP
        }
      } else {
        calculatedValue = totalDaysInCurrentMonth;
      }
    } else {
      // --- Logic for Second Month onwards ---
      const totalDaysInPreviousMonth = getDaysInMonth(previousMonthKey);

      if (previousMonthDaysResult < totalDaysInPreviousMonth) {
        calculatedValue = 0; // Liquidity exhausted in prior month
      } else {
        if (netLiquidityPosition <= 0) {
          const absDenominator = Math.abs(denominatorValue);

          if (absDenominator === 0) {
            calculatedValue = 0;
          } else {
            const fractionalDays =
              (-netLiquidityPosition / absDenominator) *
              totalDaysInCurrentMonth;

            calculatedValue = Math.floor(
              totalDaysInCurrentMonth - fractionalDays
            );
          }
        } else {
          calculatedValue = totalDaysInCurrentMonth;
        }
      }
    }

    const result = Math.max(calculatedValue, 0);

    daysOfLiquidityRow[currentMonthKey] = result === 0 ? "" : result;

    previousMonthDaysResult = result;
    previousMonthKey = currentMonthKey;
  });

  return daysOfLiquidityRow;
};

const TimelineTable = ({
  liquidityModelTableColumns,
  liquidityModelTableDatas,
  investmentsTableDatas,
  sTReceivablesOnBsTableDatas,
  sTLiabilitiesOnBsTableDatas,
} = {}) => {
  const { selectedDecimalsIn } = useHedgeRatioContext();

  const dynamicDateColumns = useMemo(
    () => filterMonthlyColumns(liquidityModelTableColumns),
    [liquidityModelTableColumns]
  );

  const monthKeys = useMemo(
    () => dynamicDateColumns.map((col) => col.id),
    [dynamicDateColumns]
  );

  const fixedColumns = [
    { id: "type", label: "Type", minWidth: 200, align: "left" },
    { id: "category", label: "Category", minWidth: 200, align: "left" },
  ];

  const columns = [...fixedColumns, ...dynamicDateColumns];

  const daysInMonthRow = getDaysInMonthRow(dynamicDateColumns);
  const daysOfLiquidityRow = calculateDaysOfAvailableLiquidity(
    liquidityModelTableDatas,
    monthKeys
  );

  const nagRow = calculateNAGRow(
    liquidityModelTableDatas,
    monthKeys,
    selectedDecimalsIn
  );

  const operIncomeRow = calculateOperIncomeRow(
    liquidityModelTableDatas,
    monthKeys,
    selectedDecimalsIn
  );

  const datas = [daysOfLiquidityRow, daysInMonthRow, nagRow, operIncomeRow];

  const totalDaysOfLiquidity = useMemo(() => {
    let sum = 0;

    monthKeys.forEach((monthKey) => {
      const dayValue = daysOfLiquidityRow[monthKey];
      sum += Number(dayValue) || 0;
    });

    return sum;
  }, [daysOfLiquidityRow, monthKeys]);

  const rawShortTermAssetsSum = useMemo(() => {
    let sum = 0;

    const cashAndDeposits = investmentsTableDatas?.find(
      (item) => item?.name === "Cash & Deposits"
    );
    sum += Number(cashAndDeposits?.value) || 0;

    const mmkAndGovInv = investmentsTableDatas?.find(
      (item) => item?.name === "MMK & Gov Inv."
    );
    sum += Number(mmkAndGovInv?.value) || 0;

    const stReceivables = sTReceivablesOnBsTableDatas?.find(
      (item) => item?.receivablesName === "S/T Receivables"
    );
    sum += Number(stReceivables?.receivablesValue) || 0;

    return sum;
  }, [investmentsTableDatas, sTReceivablesOnBsTableDatas]);

  const shortTermAssetsOnBs = useMemo(() => {
    return formatAndLocalize(rawShortTermAssetsSum, selectedDecimalsIn);
  }, [rawShortTermAssetsSum, selectedDecimalsIn]);

  const rawShortTermLiabilitiesSum = useMemo(() => {
    let sum = 0;

    const stDebt = sTLiabilitiesOnBsTableDatas?.find(
      (item) => item?.liabilitiesName === "S/T Debt"
    );
    sum += Number(stDebt?.liabilitiesValue) || 0;

    return sum;
  }, [sTLiabilitiesOnBsTableDatas]);

  const shortTermLiabilitiesOnBs = useMemo(() => {
    return formatAndLocalize(rawShortTermLiabilitiesSum, selectedDecimalsIn);
  }, [rawShortTermLiabilitiesSum, selectedDecimalsIn]);

  const balanceSheetLiquidity =
    (rawShortTermAssetsSum / rawShortTermLiabilitiesSum) * 100;

  return (
    <Grid container spacing={2} sx={{ marginBottom: 2 }}>
      <Grid item xs={12}>
        {!mainResponse ? (
          <Item>
            <Typography
              variant="p"
              component="p"
              sx={{
                color: "#adadad",
                minHeight: "250px",
              }}
            >
              There was an error in fetching the data. Please try again later.
            </Typography>
          </Item>
        ) : !mainResponse?.length ? (
          <Item>
            <Typography
              variant="p"
              component="p"
              sx={{
                color: "#adadad",
                minHeight: "250px",
              }}
            >
              No data available for the selected filters.
            </Typography>
          </Item>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {/* Timeline Table */}
              <ColumnGroupingTable
                columns={columns}
                datas={datas}
                loading={false}
                disablePagination={true}
                applyThousandsSeparator={true}
                tableHeight={200}
                rowSpanKey="type" // To merge and center vertically
              />
            </Grid>
            <Grid
              item
              xs={12}
              lg={6}
              sx={{
                borderRight: { lg: "1px solid lightgrey", xs: "none" },
                padding: "25px",
              }}
            >
              <Typography
                variant="body1"
                component="p"
                sx={{
                  fontWeight: "bold",
                  color: "#B83211",
                  backgroundColor: "#FFFF00",
                  border: "1px solid #000000",
                  padding: "4px",
                  display: "inline-block",
                  width: "100%",
                }}
              >
                DAF 2:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{totalDaysOfLiquidity} Days
              </Typography>
              <Typography
                variant="body1"
                component="p"
                sx={{ marginTop: 2, fontWeight: "bold", color: "#B83211" }}
              >
                Target Minimum Cash Buffer:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                {formatAndLocalize(
                  HARDCODED_MINIMUM_CASH_BUFER,
                  selectedDecimalsIn
                )}
              </Typography>
              <Typography
                variant="body1"
                component="p"
                sx={{ marginTop: 2, fontWeight: "bold", color: "#B83211" }}
              >
                Avg. Actual Cash Buffer (maturity &le;
                30d):&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                {formatAndLocalize(
                  HARDCODED_AVG_ACTUAL_CASH_BUFFER,
                  selectedDecimalsIn
                )}
              </Typography>
              <Typography
                variant="body1"
                component="p"
                sx={{ marginTop: 2, fontWeight: "bold", color: "#B83211" }}
              >
                Cash Buffer In Compliance?:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                {HARDCODED_AVG_ACTUAL_CASH_BUFFER > HARDCODED_MINIMUM_CASH_BUFER
                  ? "YES"
                  : "NO"}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              lg={6}
              sx={{
                padding: "25px",
              }}
            >
              <Typography
                variant="body1"
                component="p"
                sx={{ marginTop: 1, fontWeight: "bold", color: "#B83211" }}
              >
                Short-Term Assets on BS :&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                {shortTermAssetsOnBs}
              </Typography>
              <Typography
                variant="body1"
                component="p"
                sx={{ marginTop: 2, fontWeight: "bold", color: "#B83211" }}
              >
                Short-Term Liabilities on BS:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                {shortTermLiabilitiesOnBs}
              </Typography>
              <Typography
                variant="body1"
                component="p"
                sx={{ marginTop: 2, fontWeight: "bold", color: "#B83211" }}
              >
                Balance Sheet Liquidity:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                {balanceSheetLiquidity?.toFixed(1)}%
              </Typography>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default TimelineTable;
